import {
    type AgeCategory,
    AgeCategoryType,
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";

export interface AgeCategoryDefaults {
    color: string;
}

export interface AgeCategoryCountInput {
    ageCategoryId?: string;
    count?: number;
}

export interface ExactAgeCategoryCountInput extends AgeCategoryCountInput {
    type?: AgeCategoryType;
}

export type AgeCategoryTypeCounts = Partial<Record<AgeCategoryType, number>>;

export interface AdultChildGuestCounts {
    adultCount: number;
    childCount: number;
    totalCount: number;
    hasCompleteBreakdown: boolean;
}

export interface ResolvedAgeCategoryCount extends Pick<
    AgeCategory,
    "id" | "name" | "type" | "alias" | "color"
> {
    count: number;
}

export const AGE_CATEGORY_DEFAULTS: Record<AgeCategoryType, AgeCategoryDefaults> = {
    [AgeCategoryType.ADULT]: {
        color: "#2563EB",
    },
    [AgeCategoryType.TEENAGER]: {
        color: "#0891B2",
    },
    [AgeCategoryType.CHILD]: {
        color: "#D97706",
    },
    [AgeCategoryType.INFANT]: {
        color: "#7C3AED",
    },
};

export const DEFAULT_AGE_CATEGORY_TYPES = [
    AgeCategoryType.ADULT,
    AgeCategoryType.TEENAGER,
    AgeCategoryType.CHILD,
    AgeCategoryType.INFANT,
];

export const hasConfiguredAgeCategoryAliases = (
    ageCategories: AgeCategory[],
): boolean => ageCategories.some(ageCategory => Boolean(ageCategory.alias?.trim()));

export const resolveDefaultAgeCategories = (
    ageCategories: AgeCategory[],
): AgeCategory[] => DEFAULT_AGE_CATEGORY_TYPES.flatMap(type => {
    const candidates = ageCategories.filter(ageCategory => (
        ageCategory.id
        && ageCategory.type === type
        && ageCategory.archived !== true
    ));
    const defaultName = type.toLowerCase();
    const defaultCategory = candidates.find(ageCategory => (
        ageCategory.name?.trim().toLowerCase() === defaultName
    ))
        ?? candidates.find(ageCategory => !ageCategory.alias?.trim())
        ?? candidates[0];

    return defaultCategory ? [defaultCategory] : [];
});

export const resolveGuestCountsFromTypeTotals = (
    ageCategories: AgeCategory[],
    countsByType: AgeCategoryTypeCounts,
    exactCounts: ExactAgeCategoryCountInput[] = [],
): AgeCategoryCountInput[] => {
    const categoriesById = new Map(
        ageCategories.flatMap(ageCategory => ageCategory.id
            ? [[ageCategory.id, ageCategory] as const]
            : []),
    );
    const remainingCounts = new Map(
        DEFAULT_AGE_CATEGORY_TYPES.map(type => [type, countsByType[type] ?? 0]),
    );
    const resolvedCounts = new Map<string, number>();

    exactCounts.forEach(({ageCategoryId, count = 0, type}) => {
        if (!ageCategoryId || count <= 0) return;

        resolvedCounts.set(
            ageCategoryId,
            (resolvedCounts.get(ageCategoryId) ?? 0) + count,
        );

        const categoryType = type ?? categoriesById.get(ageCategoryId)?.type;
        if (categoryType) {
            remainingCounts.set(
                categoryType,
                Math.max(0, (remainingCounts.get(categoryType) ?? 0) - count),
            );
        }
    });

    resolveDefaultAgeCategories(ageCategories).forEach(ageCategory => {
        if (!ageCategory.id || !ageCategory.type) return;

        const remainingCount = remainingCounts.get(ageCategory.type) ?? 0;
        if (remainingCount <= 0) return;

        resolvedCounts.set(
            ageCategory.id,
            (resolvedCounts.get(ageCategory.id) ?? 0) + remainingCount,
        );
    });

    return Array.from(resolvedCounts, ([ageCategoryId, count]) => ({
        ageCategoryId,
        count,
    }));
};

export const resolveAdultChildGuestCounts = (
    ageCategories: AgeCategory[],
    guestCounts: AgeCategoryCountInput[],
): AdultChildGuestCounts => {
    const categoryTypesById = new Map(
        ageCategories.flatMap(category => category.id
            ? [[category.id, category.type] as const]
            : []),
    );
    let adultCount = 0;
    let childCount = 0;
    let totalCount = 0;

    guestCounts.forEach(({ageCategoryId, count = 0}) => {
        totalCount += count;
        if (!ageCategoryId || !categoryTypesById.has(ageCategoryId)) return;

        const categoryType = categoryTypesById.get(ageCategoryId);
        if (!categoryType || categoryType === AgeCategoryType.ADULT) {
            adultCount += count;
        } else {
            childCount += count;
        }
    });

    return {
        adultCount,
        childCount,
        totalCount,
        hasCompleteBreakdown: totalCount > 0 && adultCount + childCount === totalCount,
    };
};

export const resolveAgeCategoryAppearance = (
    ageCategory: Pick<AgeCategory, "type" | "alias" | "color">
): {alias: string | null; color: string | null} => {
    const defaults = ageCategory.type
        ? AGE_CATEGORY_DEFAULTS[ageCategory.type]
        : undefined;

    return {
        alias: ageCategory.alias ?? null,
        color: ageCategory.color ?? defaults?.color ?? null,
    };
};

export const aggregateAgeCategoryCounts = (
    categories: ResolvedAgeCategoryCount[],
): ResolvedAgeCategoryCount[] => {
    const aggregatedCategories = new Map<string, ResolvedAgeCategoryCount>();
    categories.forEach(ageCategory => {
        const hasOverride = Boolean(ageCategory.alias?.trim() || ageCategory.color?.trim());
        const groupByType = !hasOverride && Boolean(ageCategory.type);
        const key = hasOverride
            ? ageCategory.id
                ?? [ageCategory.type, ageCategory.name, ageCategory.alias, ageCategory.color].join(":")
            : `default:${ageCategory.type ?? ageCategory.id ?? ageCategory.name}`;
        const existingCategory = aggregatedCategories.get(key);
        aggregatedCategories.set(key, {
            ...(groupByType ? {type: ageCategory.type} : ageCategory),
            count: (existingCategory?.count ?? 0) + ageCategory.count,
        });
    });
    return Array.from(aggregatedCategories.values());
};

export const resolveAgeCategoryCounts = (
    ageCategories: AgeCategory[],
    ageCategoryCounts: AgeCategoryCountInput[],
): ResolvedAgeCategoryCount[] | null => {
    const ageCategoriesById = new Map(
        ageCategories.flatMap(ageCategory => ageCategory.id
            ? [[ageCategory.id, ageCategory] as const]
            : []),
    );
    const positiveCategoryCounts = ageCategoryCounts.filter(({count}) => (count ?? 0) > 0);
    const resolvedCategories = positiveCategoryCounts.flatMap(({ageCategoryId, count}) => {
        const ageCategory = ageCategoryId ? ageCategoriesById.get(ageCategoryId) : undefined;
        return ageCategory ? [{...ageCategory, count: count ?? 0}] : [];
    });

    if (resolvedCategories.length !== positiveCategoryCounts.length) return null;
    return aggregateAgeCategoryCounts(resolvedCategories);
};

const ageOnDate = (birthday: string, referenceDate: Date): number | null => {
    const [year, month, day] = birthday.split("-").map(Number);
    if (!year || !month || !day) return null;

    let age = referenceDate.getFullYear() - year;
    const birthdayHasPassed = referenceDate.getMonth() + 1 > month
        || (referenceDate.getMonth() + 1 === month && referenceDate.getDate() >= day);
    if (!birthdayHasPassed) age -= 1;

    return age >= 0 ? age : null;
};

export const resolveAgeCategoryForBirthday = (
    ageCategories: AgeCategory[],
    birthday: string,
    referenceDate: Date,
): AgeCategory | undefined => {
    const age = ageOnDate(birthday, referenceDate);
    if (age === null) return undefined;

    return ageCategories
        .filter(ageCategory => ageCategory.archived !== true)
        .filter(ageCategory => (
            typeof ageCategory.minAge === "number" || typeof ageCategory.maxAge === "number"
        ))
        .filter(ageCategory => (
            (typeof ageCategory.minAge !== "number" || age >= ageCategory.minAge)
            && (typeof ageCategory.maxAge !== "number" || age <= ageCategory.maxAge)
        ))
        .sort((left, right) => {
            const leftHasOverride = Boolean(left.alias?.trim() || left.color?.trim());
            const rightHasOverride = Boolean(right.alias?.trim() || right.color?.trim());
            if (leftHasOverride !== rightHasOverride) return leftHasOverride ? -1 : 1;

            const maxAgeDifference = (left.maxAge ?? Number.MAX_SAFE_INTEGER)
                - (right.maxAge ?? Number.MAX_SAFE_INTEGER);
            if (maxAgeDifference !== 0) return maxAgeDifference;

            const minAgeDifference = (right.minAge ?? Number.MIN_SAFE_INTEGER)
                - (left.minAge ?? Number.MIN_SAFE_INTEGER);
            if (minAgeDifference !== 0) return minAgeDifference;

            return (left.name ?? "").localeCompare(right.name ?? "");
        })[0];
};

/**
 * Translate guest counts onto the age categories a service actually offers — used when a new
 * reservation draft carries counts copied from a stay, from another sector, or from a previously
 * selected service. A category the service already offers is kept; otherwise the service's first
 * category of the same type stands in.
 *
 * Two different source categories are never folded into the same one, because that destroys the
 * guest mix: a stay banded into Kind 2 + Kind 3 must not arrive in the dialog as "2 children".
 * When a fold-together would happen, those counts keep their own category instead — the dialog
 * lists the categories present on the reservation alongside the service's own, so they stay
 * visible and editable.
 */
export const mapGuestCountsToServiceAgeCategories = (
    guestCounts: AgeCategoryCountInput[],
    serviceAgeCategories: AgeCategory[],
    allAgeCategories: AgeCategory[],
): AgeCategoryCountInput[] => {
    const serviceCategoryIds = new Set(
        serviceAgeCategories.map(category => category.id).filter((id): id is string => Boolean(id))
    )
    const categoriesById = new Map(
        allAgeCategories.flatMap(category => category.id
            ? [[category.id, category] as const]
            : [])
    )
    const firstServiceCategoryIdByType = new Map<AgeCategoryType, string>()
    serviceAgeCategories.forEach(category => {
        if (category.id && category.type && !firstServiceCategoryIdByType.has(category.type)) {
            firstServiceCategoryIdByType.set(category.type, category.id)
        }
    })

    // Where would each count land?
    const plannedCounts = guestCounts.map(guestCount => {
        const sourceId = guestCount.ageCategoryId
        const sourceCategory = sourceId ? categoriesById.get(sourceId) : undefined
        const targetId = sourceId && serviceCategoryIds.has(sourceId)
            ? sourceId
            : sourceCategory?.type
                ? firstServiceCategoryIdByType.get(sourceCategory.type)
                : undefined
        return {guestCount, sourceId, targetId}
    })

    // A target that more than one source category would land on would merge them, so those keep
    // their own category.
    const sourceIdsByTargetId = new Map<string, Set<string>>()
    plannedCounts.forEach(({sourceId, targetId}) => {
        if (!sourceId || !targetId) return
        const sourceIds = sourceIdsByTargetId.get(targetId) ?? new Set<string>()
        sourceIds.add(sourceId)
        sourceIdsByTargetId.set(targetId, sourceIds)
    })

    const totalsByCategoryId = new Map<string, number>()
    const unresolvedCounts: AgeCategoryCountInput[] = []
    let didRemap = false

    plannedCounts.forEach(({guestCount, sourceId, targetId}) => {
        const wouldMerge = Boolean(targetId)
            && targetId !== sourceId
            && (sourceIdsByTargetId.get(targetId!)?.size ?? 0) > 1
        const resolvedId = wouldMerge ? sourceId : targetId

        if (!resolvedId) {
            unresolvedCounts.push(guestCount)
            return
        }

        didRemap ||= resolvedId !== sourceId
        totalsByCategoryId.set(
            resolvedId,
            (totalsByCategoryId.get(resolvedId) ?? 0) + (guestCount.count ?? 0)
        )
    })

    if (!didRemap) return guestCounts

    // Service categories first, in the order the service declares them, then the ones that kept
    // their own category.
    const mappedCounts = serviceAgeCategories.flatMap(category => {
        if (!category.id || !totalsByCategoryId.has(category.id)) return []
        return [{
            ageCategoryId: category.id,
            count: totalsByCategoryId.get(category.id),
        }]
    })
    const keptCounts = Array.from(totalsByCategoryId)
        .filter(([ageCategoryId]) => !serviceCategoryIds.has(ageCategoryId))
        .map(([ageCategoryId, count]) => ({ageCategoryId, count}))

    return [...mappedCounts, ...keptCounts, ...unresolvedCounts]
};
